// quiet-hours-scheduler.ts
//
// §18 quiet hours + max_sessions_per_hour (phase-08 Agent H, tests 21-24).
// Both config keys already live in schemas/config-schema.ts (P4) with §18
// defaults (`quiet_hours: null`, `timezone: "Asia/Ho_Chi_Minh"`,
// `max_sessions_per_hour: 0`) — this module only READS the injected config,
// never hardcodes a threshold (AST gate extended to cover this file).
//
// Pure functions only: this ONLY decides whether a new dispatch attempt is
// blocked. It never sleeps, never loops — the control loop (inbox/outbox/
// heartbeat polling in supervisor-run-until-idle.ts) keeps running exactly
// as it does through a quota sleep; quiet hours only gates the ONE call site
// that would start a new attempt (§18: "chỉ chặn dispatch attempt mới").
//
// Timezone math via `Intl.DateTimeFormat` with the IANA `timeZone` option —
// NOT manual UTC-offset arithmetic (that silently breaks across DST
// transitions, per quota-sleep-scheduler.ts's own review history). No
// external tz library: Node's ICU-backed Intl already carries the full IANA
// database.
import type { AutopilotConfig } from "./schemas/config-schema.js";

export type QuietHoursConfig = Pick<AutopilotConfig, "quiet_hours">;
export type SessionCapConfig = Pick<AutopilotConfig, "max_sessions_per_hour">;

export interface QuietHoursDecision {
  blocked: boolean;
  /** ISO instant quiet hours end for the window containing `now` — only set when blocked. */
  resumeAt?: string;
}

export interface SessionCapDecision {
  blocked: boolean;
  countInWindow: number;
  /** ISO instant the window frees up — oldest in-window attempt + 60min. Only set when blocked. */
  resumeAt?: string;
}

const SLIDING_WINDOW_MS = 60 * 60 * 1000;

interface LocalWallClock {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  second: number;
}

/** Reads `now`'s wall-clock time IN the given IANA zone via Intl — DST-safe. */
function wallClockInZone(now: Date, timeZone: string): LocalWallClock {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const parts = formatter.formatToParts(now);
  const get = (type: string): number => {
    const part = parts.find((p) => p.type === type);
    if (!part) throw new Error(`quiet-hours-scheduler: Intl part "${type}" bị thiếu.`);
    // hour12:false can still emit "24" for midnight in some ICU versions —
    // normalize to 0 so downstream minute-of-day math stays in [0, 1440).
    const value = Number(part.value);
    return type === "hour" && value === 24 ? 0 : value;
  };
  return {
    year: get("year"),
    month: get("month"),
    day: get("day"),
    hour: get("hour"),
    minute: get("minute"),
    second: get("second"),
  };
}

/** Finds the UTC instant corresponding to a given wall-clock time IN `timeZone`, via bisection against Intl (no manual offset table needed, DST-correct by construction). */
function zonedWallClockToUtc(
  wall: { year: number; month: number; day: number; hour: number; minute: number },
  timeZone: string,
): Date {
  // Start from a naive UTC guess, then correct for the zone's offset at that
  // instant. One correction pass is enough for all real IANA zones (no zone
  // has an offset that changes by more than a few hours within a single
  // correction step), but we iterate twice to be safe across DST edges.
  let guess = Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, 0);
  for (let i = 0; i < 3; i++) {
    const observed = wallClockInZone(new Date(guess), timeZone);
    const observedMs = Date.UTC(
      observed.year,
      observed.month - 1,
      observed.day,
      observed.hour,
      observed.minute,
      observed.second,
    );
    const diff =
      Date.UTC(wall.year, wall.month - 1, wall.day, wall.hour, wall.minute, 0) - observedMs;
    if (diff === 0) break;
    guess += diff;
  }
  return new Date(guess);
}

function minuteOfDay(hhmm: string): number {
  const match = hhmm.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) throw new Error(`quiet-hours-scheduler: giờ "${hhmm}" không đúng dạng HH:MM.`);
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return hour * 60 + minute;
}

/**
 * Blocks a NEW dispatch attempt when `now` (in the configured IANA zone)
 * falls inside `[start, end)`, correctly handling a window that spans
 * midnight (start > end, e.g. 22:00 -> 06:00). `quiet_hours: null` (default)
 * never blocks.
 */
export function evaluateQuietHours(config: QuietHoursConfig, now: Date): QuietHoursDecision {
  const quietHours = config.quiet_hours;
  if (quietHours === null || quietHours === undefined) return { blocked: false };

  const { start, end, timezone } = quietHours;
  const startMin = minuteOfDay(start);
  const endMin = minuteOfDay(end);
  const wall = wallClockInZone(now, timezone);
  const nowMin = wall.hour * 60 + wall.minute;

  const spansMidnight = startMin > endMin;
  const insideWindow = spansMidnight
    ? nowMin >= startMin || nowMin < endMin
    : nowMin >= startMin && nowMin < endMin;

  if (!insideWindow) return { blocked: false };

  // Resume = the NEXT occurrence of `end` local time, which is ALWAYS still
  // today's calendar date while inside the window: for a same-day window
  // (start < end) `insideWindow` already implies `nowMin < endMin`, so `end`
  // hasn't happened yet today; for a window spanning midnight, `end` is
  // today's date on the early-morning tail (nowMin < endMin) and tomorrow's
  // date on the evening side (nowMin >= startMin, still before midnight).
  //
  // BUG-4 fix (dogfood 260925 luồng 5): the old `endIsToday = spansMidnight
  // && nowMin < endMin` unconditionally added +1 day for every same-day
  // window (spansMidnight is always false there), even mid-window on the
  // correct calendar day — reported "mở lại lúc" 24h later than reality.
  const endIsToday = !spansMidnight || nowMin < endMin;
  const endDate = new Date(Date.UTC(wall.year, wall.month - 1, wall.day));
  if (!endIsToday) endDate.setUTCDate(endDate.getUTCDate() + 1);

  const resumeUtc = zonedWallClockToUtc(
    {
      year: endDate.getUTCFullYear(),
      month: endDate.getUTCMonth() + 1,
      day: endDate.getUTCDate(),
      hour: Math.floor(endMin / 60),
      minute: endMin % 60,
    },
    timezone,
  );

  return { blocked: true, resumeAt: resumeUtc.toISOString() };
}

/**
 * `max_sessions_per_hour = 0` (default) => unlimited, never blocks (test:
 * `test_max_sessions_per_hour_zero_means_no_session_cap`). Otherwise counts
 * attempt timestamps within a 60-minute sliding window ending at `now` and
 * blocks once that count has REACHED the cap (cap means "no more than N
 * in-window" — the (cap+1)-th attempt in the window is refused).
 */
export function evaluateSessionCap(
  config: SessionCapConfig,
  attemptTimestampsIso: readonly string[],
  now: Date,
): SessionCapDecision {
  const cap = config.max_sessions_per_hour;
  const windowStart = now.getTime() - SLIDING_WINDOW_MS;
  const inWindowMs = attemptTimestampsIso
    .map((iso) => new Date(iso).getTime())
    .filter((t) => t > windowStart && t <= now.getTime());
  const countInWindow = inWindowMs.length;

  if (cap === 0) return { blocked: false, countInWindow };
  const blocked = countInWindow >= cap;
  if (!blocked) return { blocked: false, countInWindow };

  // BUG-3 fix (dogfood recheck 260925): the window only frees up once its
  // OLDEST member ages past the 60-minute mark — every other guard (quiet
  // hours) already tells the operator when to retry; this one silently
  // didn't. `inWindowMs` is non-empty here (countInWindow >= cap > 0).
  const oldestInWindowMs = Math.min(...inWindowMs);
  const resumeAt = new Date(oldestInWindowMs + SLIDING_WINDOW_MS).toISOString();
  return { blocked: true, countInWindow, resumeAt };
}
